import { DEFAULT_CONVERSATION_ID } from "@repo/contract/iam";
import { collapseCitations } from "./citations";
import { toBlocks } from "./markdown";
/**
 * The trainer's last line, cached on the device for home's strip (`D156`).
 *
 * ── WHY A CACHE AND NOT A READ ──────────────────────────────────────────────
 * The transcript lives in a Durable Object (`D11`) behind a ticket purchase,
 * and `TrainerSocket` holds its copy in instance memory that dies with the
 * chat screen. Reading one sentence from there on every home mount is a
 * ticket, a socket and an object wake for the most-opened screen in the app,
 * which is the fetch `T42` refused and `D156` rejected. So the chat screen
 * writes the sentence here as each answer COMPLETES, and home reads a string.
 *
 * ── THE FIRST SENTENCE, OF A COMPLETE ANSWER ONLY ───────────────────────────
 * `state === "complete"` means the end marker arrived and every structural
 * check passed. A truncated answer's first sentence may be whole, but it is
 * the opening of an answer `D77` refused to show, and the strip would then be
 * promising a conversation the transcript does not contain.
 *
 * ── KEYED BY ACCOUNT (AND, SINCE T44, BY CONVERSATION), LIKE THE INTAKE ────
 * `signOut()` clears the credential and touches no Async Storage key of ours.
 * The key carries the account id, so signing out and in as somebody else
 * finds nothing rather than the first person's trainer talking on the second
 * person's home — which the intake store guards against with a check inside
 * the value, and is guarded here by the key itself.
 *
 * ── THE STORE IS A PORT ─────────────────────────────────────────────────────
 * Injected rather than mocked (`CLAUDE.md` → Testing): Async Storage cannot
 * load in a Node test, and the sentence rule is the one decision here that
 * can be wrong.
 *
 * And REQUIRED, since the move into `@repo/client`: the Async Storage default
 * this file used to fall back on belongs to a runtime this package must not
 * know about, so the app supplies the store (`localStorage` on the web,
 * Capacitor Preferences on the device). A silent no-op default would have been
 * worse than none — the strip would simply never fill, with nothing red.
 */
const KEY_PREFIX = "mypocketgym.chat.last_answer.";
/** Long enough for a sentence, short enough for a strip — cut on a word. */
export const MAX_LINE_LENGTH = 140;
/**
 * ── KEYED BY ACCOUNT AND CONVERSATION (plan D32, T44) ───────────────────────
 * Keyed by account alone, the strip showed B's last answer as "the trainer's
 * last line" while the person was reading A. The conversation id is the second
 * segment; `CONVERSATION_ID_PATTERN` admits no `.`, so no id can make one key
 * a prefix-collision of another's.
 */
export function lastAnswerKey(accountId, conversationId) {
    return `${KEY_PREFIX}${accountId}.${conversationId}`;
}
/** The pre-T44 key: account only. Read by the migration and nothing else. */
export function legacyLastAnswerKey(accountId) {
    return `${KEY_PREFIX}${accountId}`;
}
function parseStoredLine(raw) {
    try {
        const parsed = JSON.parse(raw);
        if (typeof parsed === "object" &&
            parsed !== null &&
            typeof parsed.line === "string" &&
            typeof parsed.at === "number") {
            const { at, line } = parsed;
            return line.trim() === "" ? null : { at, line };
        }
    }
    catch {
        // Not JSON — see below.
    }
    // A bare string is the shape every build before T44 wrote. It is only ever
    // met under the legacy key, which the migration converts, but reading it as
    // a line with no time is the answer that cannot show the wrong thing.
    return raw.trim() === "" ? null : { at: 0, line: raw };
}
/**
 * Adopt the pre-T44 account-only line as `default`'s — the only conversation
 * any earlier build spoke. Idempotent: the legacy key is removed on the way
 * out, and a line already under the new key (written by this build, so newer)
 * is kept.
 */
export async function migrateLegacyLastAnswer(accountId, store) {
    try {
        const legacy = await store.getItem(legacyLastAnswerKey(accountId));
        if (legacy === null) {
            return;
        }
        const parsed = parseStoredLine(legacy);
        const key = lastAnswerKey(accountId, DEFAULT_CONVERSATION_ID);
        if (parsed !== null && (await store.getItem(key)) === null) {
            await store.setItem(key, JSON.stringify(parsed));
        }
        await store.removeItem(legacyLastAnswerKey(accountId));
    }
    catch {
        // Storage refused; the next call tries again.
    }
}
/**
 * The end of a sentence: `.`, `!` or `?`, any closing quote or bracket after
 * it, then whitespace or the end of the text. The lookahead is what keeps
 * `2.5 kg` whole. An abbreviation such as `e.g.` does end the line early —
 * a splitter that knew every abbreviation in two languages would be a
 * library, and a line cut one clause short is a teaser rather than a bug.
 */
const SENTENCE_END = /^([\s\S]*?[.!?]["'”’)\]]*)(?=\s|$)/;
/** `[1]` — an inline citation collapsed by `collapseCitations`. */
const CITATION_MARK = /\s*\[\d+\]/g;
/** `# Heading` — not rendered by the chat either, but a `#` on home is noise. */
const HEADING_MARK = /^#{1,6}\s+/;
/**
 * The first sentence of an answer, as prose: no `**`, no bullet marker, no
 * citation number, no sources block. `null` when there is no prose at all.
 *
 * `toBlocks` is the chat's own reader, so what home shows is the first thing
 * the bubble shows — bold stripped to its words, a leading bullet to its
 * text. A first block longer than {@link MAX_LINE_LENGTH} with no sentence
 * end inside it is cut on the last space before the limit and marked with an
 * ellipsis, so the strip never ends mid-word.
 */
export function firstSentence(text) {
    const [first] = toBlocks(collapseCitations(text));
    if (first === undefined) {
        return null;
    }
    const prose = first.spans
        .map((span) => span.text)
        .join("")
        .replace(CITATION_MARK, "")
        .replace(HEADING_MARK, "")
        .replace(/\s+/g, " ")
        .trim();
    if (prose === "") {
        return null;
    }
    const sentence = SENTENCE_END.exec(prose)?.[1] ?? prose;
    if (sentence.length <= MAX_LINE_LENGTH) {
        return sentence;
    }
    const cut = sentence.lastIndexOf(" ", MAX_LINE_LENGTH - 1);
    return `${sentence.slice(0, cut > 0 ? cut : MAX_LINE_LENGTH - 1).trimEnd()}…`;
}
/**
 * The newest assistant message whose answer completed, or `null`. Read from
 * the end: during a stream the last message is the draft, and the line to
 * show is still the previous answer's.
 */
export function lastCompleteAnswer(messages) {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
        const message = messages[index];
        if (message !== undefined &&
            message.role === "assistant" &&
            message.state === "complete" &&
            message.hidden !== true) {
            return message;
        }
    }
    return null;
}
/**
 * Write the answer's first sentence for this account and conversation. An
 * answer with no prose — a widget-only turn — leaves the previous line in
 * place rather than replacing it with nothing.
 */
export async function rememberLastAnswer(accountId, conversationId, text, now, store) {
    const line = firstSentence(text);
    if (line === null) {
        return;
    }
    await migrateLegacyLastAnswer(accountId, store);
    const stored = { at: now, line };
    try {
        await store.setItem(lastAnswerKey(accountId, conversationId), JSON.stringify(stored));
    }
    catch {
        // Storage refused. The answer is on the chat screen regardless; only
        // home's strip stays on its previous line, and a throw here would land
        // in a render effect with nothing to catch it.
    }
}
/** The transcript was cleared (`D124`): the strip goes back to its prompt. */
export async function forgetLastAnswer(accountId, conversationId, store) {
    await migrateLegacyLastAnswer(accountId, store);
    try {
        await store.removeItem(lastAnswerKey(accountId, conversationId));
    }
    catch {
        // As above. A stale line names a conversation that is now empty, which
        // the chat screen corrects the moment it is opened.
    }
}
async function readStoredLine(accountId, conversationId, store) {
    try {
        const raw = await store.getItem(lastAnswerKey(accountId, conversationId));
        return raw === null ? null : parseStoredLine(raw);
    }
    catch {
        return null;
    }
}
/** The cached line for this conversation, or `null` — none, or refused. */
export async function readLastAnswer(accountId, conversationId, store) {
    await migrateLegacyLastAnswer(accountId, store);
    return (await readStoredLine(accountId, conversationId, store))?.line ?? null;
}
/**
 * The newest line among `conversationIds`, for Today's strip (T60), or `null`.
 *
 * Reads each conversation's entry and compares the time stored beside the
 * line — the simpler of the two designs T44 offered, since a separate "most
 * recent" index is a second write per answer and a second thing a `clear`
 * must correct. The cost is one read per id, so the caller passes the few
 * conversations it cares about: `GET /me/conversations` is already ordered
 * by last activity, and its first handful is enough. Ties go to the id listed
 * first. A migrated line (time `0`) loses to any line written since.
 */
export async function readMostRecentLastAnswer(accountId, conversationIds, store) {
    await migrateLegacyLastAnswer(accountId, store);
    const lines = await Promise.all(conversationIds.map((id) => readStoredLine(accountId, id, store)));
    let best = null;
    for (const [index, stored] of lines.entries()) {
        const conversationId = conversationIds[index];
        if (stored === null || conversationId === undefined) {
            continue;
        }
        if (best === null || stored.at > best.at) {
            best = { at: stored.at, conversationId, line: stored.line };
        }
    }
    return best === null
        ? null
        : { conversationId: best.conversationId, line: best.line };
}
//# sourceMappingURL=last-answer.js.map